import { addDays, type DateRange, inRange, nextWeek, thisMonth, thisWeek } from './calendar';
import {
  type DuePreset,
  type Filter,
  type FilterableItem,
  type FilterCondition,
  type FilterContext,
  ME,
  type TimelinePreset,
  UNASSIGNED,
} from './model';
import { textContains } from './text';

/** Done means the status label's category is `done` (TSK-8). */
export function isDone(item: Pick<FilterableItem, 'statusId'>, ctx: FilterContext): boolean {
  return ctx.labels.get(item.statusId)?.category === 'done';
}

/** Past due and not done (TSK-7, DSH-3). */
export function isOverdue(
  item: Pick<FilterableItem, 'dueDate' | 'statusId'>,
  ctx: FilterContext,
): boolean {
  return item.dueDate !== null && item.dueDate < ctx.today && !isDone(item, ctx);
}

function dueMatches(item: FilterableItem, preset: DuePreset, ctx: FilterContext): boolean {
  const due = item.dueDate;
  switch (preset) {
    case 'none':
      return due === null;
    case 'overdue':
      return isOverdue(item, ctx);
    case 'today':
      return due === ctx.today;
    case 'tomorrow':
      return due === addDays(ctx.today, 1);
    case 'thisWeek':
      return due !== null && inRange(due, thisWeek(ctx.today));
    case 'nextWeek':
      return due !== null && inRange(due, nextWeek(ctx.today));
  }
}

function timelineRange(value: TimelinePreset | DateRange, today: string): DateRange {
  if (value === 'thisWeek') return thisWeek(today);
  if (value === 'thisMonth') return thisMonth(today);
  return value;
}

/** Owners include any of `values`; `me` and `unassigned` are resolved from the context. */
function ownersIncludeAny(item: FilterableItem, values: readonly string[], ctx: FilterContext) {
  return values.some((value) =>
    value === UNASSIGNED
      ? item.ownerIds.length === 0
      : item.ownerIds.includes(value === ME ? ctx.meId : value),
  );
}

export function matchesCondition(
  item: FilterableItem,
  condition: FilterCondition,
  ctx: FilterContext,
): boolean {
  switch (condition.field) {
    case 'status': {
      const hit = condition.value.includes(item.statusId);
      return condition.op === 'anyOf' ? hit : !hit;
    }
    case 'owner': {
      const hit = ownersIncludeAny(item, condition.value, ctx);
      return condition.op === 'anyOf' ? hit : !hit;
    }
    case 'dueDate': {
      if (condition.op === 'is') return dueMatches(item, condition.value, ctx);
      if (item.dueDate === null) return false;
      if (condition.op === 'between') return inRange(item.dueDate, condition.value);
      return condition.op === 'before'
        ? item.dueDate < condition.value
        : item.dueDate > condition.value;
    }
    case 'priority': {
      if (condition.op === 'isEmpty') return item.priorityId === null;
      const hit = item.priorityId !== null && condition.value.includes(item.priorityId);
      return condition.op === 'anyOf' ? hit : !hit;
    }
    case 'timeline': {
      if (condition.op === 'isEmpty') return item.timeline === null;
      if (item.timeline === null) return false;
      const range = timelineRange(condition.value, ctx.today);
      return item.timeline.start <= range.to && item.timeline.end >= range.from;
    }
    case 'group':
      return condition.value.includes(item.groupId);
    case 'title': {
      const hit = textContains(item.title, condition.value);
      return condition.op === 'contains' ? hit : !hit;
    }
    case 'notes': {
      if (condition.op === 'contains') return textContains(item.notes, condition.value);
      return (item.notes.trim() === '') === (condition.op === 'isEmpty');
    }
    case 'updated': {
      const since = Date.parse(ctx.now) - condition.value * 24 * 60 * 60 * 1000;
      return Date.parse(item.updatedAt) >= since;
    }
    case 'createdBy':
      return condition.value.includes(item.createdBy);
  }
}

/**
 * A condition someone is still building — no option picked, no text typed — is ignored rather than
 * matching nothing, so the board does not empty while the filter is being edited.
 */
export function isComplete(condition: FilterCondition): boolean {
  if (!('value' in condition)) return true;
  const { value } = condition;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/** All (AND) or any (OR) of the complete conditions; no complete condition matches everything. */
export function matchesFilter(item: FilterableItem, filter: Filter, ctx: FilterContext): boolean {
  const conditions = filter.conditions.filter(isComplete);
  if (conditions.length === 0) return true;
  return filter.join === 'and'
    ? conditions.every((condition) => matchesCondition(item, condition, ctx))
    : conditions.some((condition) => matchesCondition(item, condition, ctx));
}

/** TLB-1: title, key and notes. */
export function matchesSearch(item: FilterableItem, query: string): boolean {
  return (
    textContains(item.title, query) ||
    textContains(item.key, query) ||
    textContains(item.notes, query)
  );
}

/** What the toolbar narrows the board to. */
export interface ToolbarFilter {
  readonly search?: string;
  /** TLB-2: owned by any of these people (`unassigned` allowed). Empty = everyone. */
  readonly people?: readonly string[];
  readonly filter?: Filter;
}

export function isToolbarActive(toolbar: ToolbarFilter): boolean {
  return (
    (toolbar.search?.trim() ?? '') !== '' ||
    (toolbar.people?.length ?? 0) > 0 ||
    (toolbar.filter?.conditions.some(isComplete) ?? false)
  );
}

export function matchesToolbar(
  item: FilterableItem,
  toolbar: ToolbarFilter,
  ctx: FilterContext,
): boolean {
  if (toolbar.search && !matchesSearch(item, toolbar.search)) return false;
  if (toolbar.people && toolbar.people.length > 0 && !ownersIncludeAny(item, toolbar.people, ctx)) {
    return false;
  }
  return !toolbar.filter || matchesFilter(item, toolbar.filter, ctx);
}

export interface Visibility {
  /** Work items to show. */
  readonly visible: ReadonlySet<string>;
  /** Parents shown only because some of their subtasks match (TLB-8): drawn dimmed. */
  readonly dimmed: ReadonlySet<string>;
}

/**
 * TLB-8: a subtask matches on its own fields; a parent stays visible (dimmed) when only its subtasks
 * match. Subtasks of a matching parent are shown only if they match themselves.
 */
export function visibleItems(
  items: readonly FilterableItem[],
  toolbar: ToolbarFilter,
  ctx: FilterContext,
): Visibility {
  const visible = new Set<string>();
  const dimmed = new Set<string>();
  const parentsWithMatches = new Set<string>();
  for (const item of items) {
    if (!matchesToolbar(item, toolbar, ctx)) continue;
    visible.add(item.id);
    if (item.parentId !== null) parentsWithMatches.add(item.parentId);
  }
  for (const parentId of parentsWithMatches) {
    if (!visible.has(parentId)) {
      visible.add(parentId);
      dimmed.add(parentId);
    }
  }
  return { visible, dimmed };
}
